import {
  H1,
  Image,
  Link,
  Page,
  Paragraph,
  YStack,
  XStack,
  Tag,
  Text,
  Button,
  StorefrontIcon,
} from '@bounty/creators-design-system'
import { GetStoreResponseSuccess } from '@bounty/types'
import { cloudinary } from '../../../../utils/cloudinary'
import { RetailCard } from '../../components/RetailCard'
import { FeaturedProducts } from '../components/FeaturedProducts'
import { FeaturedVideos } from '../components/FeaturedVideos'
import { ShopNowButton } from './ShopNowButton'
import { SignUpModal } from './SignUpModal'
import { TagsAndCategories } from './TagsAndCategories'
import { getSocialMediaLogo } from '../../../bounty-detail/utils/contentType'
import { ShopBounties } from './ShopBounties'
import { FaCircleChevronRight } from 'react-icons/fa6'
import { useMemo, useState } from 'react'
import { RetailLocationsModal } from '../../../../components/retail/RetailLocationsModal'
import { PayoutInfoStorefront } from '../../../../components/PayoutDetailsCard'

type ShopDetailScreenMobileProps = {
  shop: GetStoreResponseSuccess['data']
}

const PROFILE_IMAGE_SIZE = 100

export const ShopDetailScreenMobile = ({
  shop,
}: ShopDetailScreenMobileProps) => {
  const [showRetailLocations, setShowRetailLocations] = useState(false)
  const restockUrl = useMemo(() => {
    if (shop) {
      if (shop.redirectUrl) return shop.redirectUrl
      if (shop.shopUrl) {
        if (shop.shopUrl.includes('https://')) return shop?.shopUrl
        else return `https://${shop.shopUrl}`
      }
    }

    return ''
  }, [shop])

  return (
    <Page addTopSpacing={false} addHorizontalSpacing={false} scroll>
      <YStack
        pb={PROFILE_IMAGE_SIZE / 2}
        position="relative"
        backgroundColor={'$neutral.200'}
      >
        <YStack position="relative">
          <Image
            src={cloudinary({
              width: 800,
              height: 200,
              mode: 'c_fill',
            })(shop.headerImgLinkPrimary!)}
            height={200}
            width="100%"
            resizeMode="cover"
          />
          <YStack
            bottom={-50}
            left={16}
            position="absolute"
            borderRadius={100000}
            backgroundColor={'$neutral.50'}
            borderColor={'$whiteAlpha.50'}
            borderWidth={4}
            overflow="hidden"
            zIndex={10}
          >
            <Image
              src={cloudinary({
                width: PROFILE_IMAGE_SIZE * 2,
                height: PROFILE_IMAGE_SIZE * 2,
              })(shop.logo!)}
              height={PROFILE_IMAGE_SIZE}
              width={PROFILE_IMAGE_SIZE}
              resizeMode="contain"
            />
          </YStack>
        </YStack>
      </YStack>
      <SignUpModal />
      <YStack
        backgroundColor={'$neutral.200'}
        pb="$6"
        pt="$4"
        space="$4"
        mb="$6"
        px="$4"
      >
        <XStack flexWrap="wrap" alignItems="center" space="$2">
          <H1 size="$2xl">{shop.name}</H1>
          <XStack
            space={'$1'}
            alignItems="center"
            justifyContent="flex-start"
            backgroundColor={'$neutral.200'}
            px="$2"
          >
            {shop.contentTypesAllowed.map((type) => {
              return (
                <Image
                  height={'$5'}
                  width={'$5'}
                  resizeMode={'contain'}
                  src={getSocialMediaLogo(type)}
                  key={`${shop.id}-${type}`}
                />
              )
            })}
          </XStack>
        </XStack>
        <Paragraph size="$sm">
          Grab your favorite products, and choose a Bounty below to get paid
          when you post!
        </Paragraph>
        <XStack flexWrap="wrap" gap="$2">
          {shop.retailLocations.length > 0 && (
            <Button
              flex={1}
              colorScheme="primary"
              event="Clicked find us in retail button"
              onPress={() => setShowRetailLocations(true)}
            >
              <XStack mr="$2" borderColor="">
                <StorefrontIcon color="white" size={19} />
              </XStack>
              Find us in retail
            </Button>
          )}
          {/* Solito doesn't have external redirects so we used basic a tag */}
          <a
            style={{
              flex: 1,
              display: 'flex',
              textDecoration: 'none',
              flexBasis: 'max-content',
            }}
            href={restockUrl}
          >
            <Button
              flex={1}
              variant="outline"
              colorScheme="primary"
              event="Clicked find us in retail button"
            >
              <XStack mr="$2" borderColor="">
                <FaCircleChevronRight size={18} />
              </XStack>
              Restock online
            </Button>
          </a>
        </XStack>
        <YStack space="$4">
          <PayoutInfoStorefront />
        </YStack>
      </YStack>
      {shop.featuredVideos?.length > 0 && (
        <YStack mb="$6">
          <FeaturedVideos featuredVideos={shop.featuredVideos} />
        </YStack>
      )}
      {shop?.briefs?.length > 0 && <ShopBounties shop={shop} />}
      {shop.featuredProducts?.length > 0 && (
        <FeaturedProducts featuredProducts={shop.featuredProducts} />
      )}
      <RetailLocationsModal
        open={showRetailLocations}
        shopName={shop?.name}
        onOpenChange={(value) => setShowRetailLocations(value)}
        locations={shop.retailLocations}
        retailLocatorUrl={shop.retailLocatorUrl}
      />
    </Page>
  )
}
